import { LoadingOverlayComponent } from '@pagopa/selfcare-common-frontend/lib';
import { trackEvent } from '@pagopa/selfcare-common-frontend/lib/services/analyticsService';
import { storageTokenOps } from '@pagopa/selfcare-common-frontend/lib/utils/storage';
import { v4 as uuidv4 } from 'uuid';
import LoginError from './pages/loginError/LoginError';
import LoginSuccess from './pages/loginSuccess/LoginSuccess';
import Logout from './pages/logout/Logout';
import { LogoutGoogle } from './pages/logoutGoogle/LogoutGoogle';
import OneIdentityAuthCallbackPage from './pages/oneIdentityAuthCallback/OneIdentityAuthCallback';
import OTPPage from './pages/otp/OTPPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import ValidateSession from './pages/ValidateSession/ValidateSession';
import {
  getOneIdentityRedirectUri,
  ROUTE_AUTH_CALLBACK,
  ROUTE_LOGIN,
  ROUTE_LOGIN_ERROR,
  ROUTE_LOGIN_SUCCESS,
  ROUTE_LOGOUT,
  ROUTE_LOGOUT_GOOGLE,
  ROUTE_OTP,
  ROUTE_PRIVACY_DISCLAIMER,
  ROUTE_TERMS_AND_CONDITION,
} from './utils/constants';
import { ENV } from './utils/env';
import {
  storageNonceOps,
  storageOnSuccessOps,
  storageRedirectURIOps,
  storageStateOps,
} from './utils/storage';

const onTermsAndCondition = () => <TermsAndConditionsPage />;

const onPrivacyDisclaimer = () => <PrivacyPolicyPage />;

const onLogout = () => <Logout />;

const onLogoutGoogle = () => <LogoutGoogle />;

const onLoginError = (queryParams: URLSearchParams) => <LoginError queryParams={queryParams} />;

/** if exists already a session */
const onAlreadyInSession = (sessionToken: string) => (
  <ValidateSession sessionToken={sessionToken} />
);

/** login request operations */
const onLoginRequest = () => {
  storageOnSuccessOps.delete();
  storageStateOps.delete();
  storageNonceOps.delete();
  storageTokenOps.delete();
  storageRedirectURIOps.delete();
  handleLoginRequestOnSuccessRequest();
  return <LoadingOverlayComponent open={true} />;
};

const onOneIdentityAuthCallback = () => <OneIdentityAuthCallbackPage />;

/** persists the onSuccess query param, if any, without touching the rest of the login flow */
const storeOnSuccessRequest = () => {
  const onSuccess: string | null = new URLSearchParams(window.location.search).get('onSuccess');
  if (onSuccess) {
    storageOnSuccessOps.write(onSuccess);
  }
};

const handleLoginRequestOnSuccessRequest = () => {
  const generateRandomUniqueString = () => uuidv4().replace(/-/g, '').slice(0, 15);
  const state = generateRandomUniqueString();
  const nonce = generateRandomUniqueString();
  const redirect_uri = getOneIdentityRedirectUri();
  const encodedRedirectUri = encodeURIComponent(redirect_uri);

  storeOnSuccessRequest();
  storageStateOps.write(state);
  storageNonceOps.write(nonce);
  storageRedirectURIOps.write(redirect_uri);

  const oneIdentityLoginUrl = `${ENV.ONE_IDENTITY.BASE_URL}/login?response_type=CODE&scope=openid&client_id=${ENV.ONE_IDENTITY.CLIENT_ID}&state=${state}&nonce=${nonce}&redirect_uri=${encodedRedirectUri}`;

  // the redirect crosses to a different domain (oneId.it), so the trackEvent call must wait for
  // mixpanel to confirm the event has been sent before navigating away, otherwise the outgoing
  // request gets cancelled by the browser as soon as the page starts unloading
  trackEvent(
    'LOGIN_INTENT',
    {
      origin: location.origin,
    },
    () => {
      window.location.assign(oneIdentityLoginUrl);
    }
  );
};

const onLoginSuccess = () => <LoginSuccess />;

const onOTPRequest = () => <OTPPage />;

const resolveRoute = (
  path: string,
  token: string | null,
  queryParams: URLSearchParams
): JSX.Element => {
  const isOTPEnabled = ENV.ENABLE_OTP === 'true';

  if (path === ROUTE_LOGOUT) {
    return onLogout();
  }
  if (path === ROUTE_LOGOUT_GOOGLE) {
    return onLogoutGoogle();
  }
  if (path === ROUTE_TERMS_AND_CONDITION) {
    return onTermsAndCondition();
  }
  if (path === ROUTE_PRIVACY_DISCLAIMER) {
    return onPrivacyDisclaimer();
  }

  if (token) {
    // a login request states its own destination: since the storage is persistent, whatever a
    // previous login left behind would otherwise hijack this one (typically back to the dashboard)
    if (path === ROUTE_LOGIN) {
      storageOnSuccessOps.delete();
    }
    // the requested destination must be honoured even when a session is already in place,
    // otherwise ValidateSession has nothing to read and falls back to the dashboard
    storeOnSuccessRequest();
    return onAlreadyInSession(token);
  }

  switch (path) {
    case ROUTE_LOGIN:
      return onLoginRequest();
    case ROUTE_AUTH_CALLBACK:
      return onOneIdentityAuthCallback();
    case ROUTE_LOGIN_SUCCESS:
      return onLoginSuccess();
    case ROUTE_LOGIN_ERROR:
      return onLoginError(queryParams);
    case ROUTE_OTP:
      return isOTPEnabled ? onOTPRequest() : onLoginRequest();
    default:
      return onLoginRequest();
  }
};

function App(): JSX.Element {
  const token = storageTokenOps.read();
  const queryParams = new URLSearchParams(window.location.search);
  return resolveRoute(window.location.pathname, token, queryParams);
}

export default App;
